/**
 * SC-739 — questions posées sur la commande entière (couverts, sauce), et non
 * sur un plat. Fonctions pures : le devis serveur reste seul juge du prix, ces
 * calculs ne servent qu'à afficher un total cohérent avant l'envoi.
 */
import type { CartOptionSaleMode, OptionGroup } from '@/types/menu';
import type { SelectedOption } from '@/types/cart';
import type { OrderMode } from '@/types/restaurant';

/** Le mode de vente tel que le nomment resource_api et sale_api. */
export function saleModeOf(mode: OrderMode): CartOptionSaleMode {
  if (mode === 'delivery') return 'livraison';
  if (mode === 'dine_in') return 'sur_place';
  return 'emporter';
}

/**
 * Les questions à poser pour ce mode de vente. Une question sans `appliesTo`
 * n'est posée nulle part : le backend refuse d'en enregistrer une, donc une
 * liste vide ne peut venir que d'une donnée abîmée — mieux vaut ne rien
 * demander que de demander partout.
 */
export function groupsForMode(
  groups: OptionGroup[] | undefined,
  mode: OrderMode,
): OptionGroup[] {
  const saleMode = saleModeOf(mode);
  return (groups ?? []).filter((g) => (g.appliesTo ?? []).includes(saleMode));
}

/**
 * SC-748 — les questions que le montant du panier débloque. `subtotalCents` est
 * le total des plats AVANT promotion et hors supplément d'option : la même base
 * que le devis serveur, sans quoi une sauce à 0,50 € ferait apparaître ici une
 * question que le serveur refuserait ensuite.
 *
 * Un seuil absent (backend antérieur) vaut « aucune condition ».
 *
 * DF-0124 — entre PALIERS, seul le plus élevé est posé : « à partir de 20 € une
 * canette offerte, à partir de 30 € une bière » doit donner UN cadeau à 33 €,
 * pas deux. Un seuil de 0 n'est pas un palier mais une question de service
 * (« Souhaitez-vous des couverts ? »), posée dès le premier euro : elle n'est
 * jamais remplacée, sans quoi un gros panier ferait disparaître l'opt-in
 * explicite des couverts jetables (loi AGEC). Ex aequo : aucun n'est plus haut,
 * les deux restent. Miroir exact du devis serveur ; c'est lui qui tranche.
 */
export function groupsForAmount(
  groups: OptionGroup[],
  subtotalCents: number,
): OptionGroup[] {
  const reached = groups.filter((g) => (g.minOrderAmount ?? 0) <= subtotalCents);
  const tiers = reached.map((g) => g.minOrderAmount ?? 0).filter((t) => t > 0);
  if (tiers.length === 0) return reached;
  const topTier = Math.max(...tiers);
  return reached.filter((g) => {
    const threshold = g.minOrderAmount ?? 0;
    return threshold === 0 || threshold === topTier;
  });
}

/**
 * SC-748 — la question verrouillée la plus proche, pour dire au client ce qui
 * lui manque. Sans elle un seuil est invisible : la question n'apparaît pas et
 * rien ne laisse deviner qu'un panier plus garni la ferait apparaître.
 *
 * `groups` doit déjà être filtré par mode de vente, pas par montant : ce sont
 * précisément les questions écartées par le montant qui nous intéressent. La
 * plus proche gagne — annoncer un palier à 80 € quand il en reste un à 30 €
 * décourage au lieu d'inciter.
 */
export function nextCartOptionHint(
  groups: OptionGroup[],
  subtotalCents: number,
): { group: OptionGroup; gapCents: number } | null {
  let nearest: { group: OptionGroup; gapCents: number } | null = null;
  for (const group of groups) {
    const threshold = group.minOrderAmount ?? 0;
    if (threshold <= subtotalCents) continue;
    const gapCents = threshold - subtotalCents;
    if (nearest === null || gapCents < nearest.gapCents) nearest = { group, gapCents };
  }
  return nearest;
}

/**
 * Les réponses des questions qui ne sont plus posées — mode de vente changé,
 * panier repassé sous un seuil. Le devis les refuserait avec un 400 opaque
 * alors que le client ne voit plus la question : on ne les envoie pas.
 */
export function selectionsForGroups(
  groups: OptionGroup[],
  selections: SelectedOption[],
): SelectedOption[] {
  const asked = new Set(groups.map((g) => g.id));
  return selections.filter((s) => asked.has(s.groupId));
}

/** Supplément total des réponses cochées, en centimes. */
export function cartOptionsTotalCents(
  groups: OptionGroup[],
  selections: SelectedOption[],
): number {
  let total = 0;
  for (const selection of selections) {
    const group = groups.find((g) => g.id === selection.groupId);
    if (!group) continue;
    for (const chosen of selection.options) {
      const option = group.options.find((o) => o.id === chosen.id);
      if (option) total += option.priceDelta * chosen.qty;
    }
  }
  return total;
}

/**
 * Les questions obligatoires restées sans réponse. Le devis les refuserait avec
 * un 400 opaque ; on bloque le bouton et on montre lesquelles.
 */
export function unansweredRequiredGroups(
  groups: OptionGroup[],
  selections: SelectedOption[],
): OptionGroup[] {
  return groups.filter((group) => {
    const min = group.min ?? 0;
    if (min < 1) return false;
    const answered = selections.find((s) => s.groupId === group.id);
    const count = (answered?.options ?? []).reduce((sum, o) => sum + o.qty, 0);
    return count < min;
  });
}

/** Nombre de réponses cochées dans une question (pour le plafond max). */
export function selectedCount(selection: SelectedOption | undefined): number {
  return (selection?.options ?? []).reduce((sum, o) => sum + o.qty, 0);
}

/** Forme attendue par sale_api / resource_api. */
export function toWireCartOptions(
  selections: SelectedOption[],
): { option_value_uuid: string; qty: number }[] {
  return selections.flatMap((s) =>
    s.options.map((o) => ({ option_value_uuid: o.id, qty: o.qty })),
  );
}

/**
 * SC-1438 — les cadeaux du panier, pour la fenêtre « Mes cadeaux ». Un cadeau
 * est une question à palier (seuil > 0) ; une question de service (couverts)
 * n'en est pas un et reste à la page de paiement.
 *
 * `groups` doit déjà être filtré par mode de vente. `unlocked` suit
 * `groupsForAmount` — entre paliers atteints seul le plus élevé se choisit,
 * comme au devis ; `locked` va du plus proche au plus lointain.
 */
export function giftTiers(
  groups: OptionGroup[],
  subtotalCents: number,
): { unlocked: OptionGroup[]; locked: { group: OptionGroup; gapCents: number }[] } {
  const unlocked = groupsForAmount(groups, subtotalCents).filter(
    (g) => (g.minOrderAmount ?? 0) > 0,
  );
  const locked = groups
    .filter((g) => (g.minOrderAmount ?? 0) > subtotalCents)
    .map((group) => ({ group, gapCents: (group.minOrderAmount ?? 0) - subtotalCents }))
    .sort((a, b) => a.gapCents - b.gapCents);
  return { unlocked, locked };
}

/**
 * La réponse d'une question après un clic sur `optionId`. Recliquer retire la
 * réponse ; une question à choix unique la remplace ; une question déjà pleine
 * refuse (`null`) plutôt que de retirer une réponse que le client vient de
 * cocher.
 */
export function toggleCartOption(
  group: OptionGroup,
  current: SelectedOption | undefined,
  optionId: string,
): SelectedOption | null {
  const chosen = current?.options ?? [];
  if (chosen.some((o) => o.id === optionId)) {
    return { groupId: group.id, options: chosen.filter((o) => o.id !== optionId) };
  }
  const max = group.max ?? 1;
  if (max <= 1) return { groupId: group.id, options: [{ id: optionId, qty: 1 }] };
  if (selectedCount(current) >= max) return null;
  return { groupId: group.id, options: [...chosen, { id: optionId, qty: 1 }] };
}
